import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert,
  TextField,
} from '@mui/material';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { userApi, type SavedSearchCreateBody } from '../../services/userApi';
import { useAuthStore } from '../../stores/authStore';

/**
 * Serialize the current search URL params into the {query, filters}
 * shape SavedSearch stores. `q` becomes `query`; everything else
 * (except pagination) becomes `filters`, preserving multi-value keys
 * as arrays. This round-trips with the dashboard's `toSearchUrl`
 * helper, which re-appends arrays via `append` and scalars via `set`.
 */
export function searchParamsToSavedSearch(searchParams: URLSearchParams): {
  query: string | null;
  filters: Record<string, string | string[]>;
} {
  const filters: Record<string, string | string[]> = {};
  for (const key of new Set(Array.from(searchParams.keys()))) {
    if (key === 'q' || key === 'page') continue;
    const all = searchParams.getAll(key).filter((v) => v !== '');
    if (all.length === 0) continue;
    const first = all[0];
    filters[key] = all.length > 1 ? all : (first as string);
  }
  const q = searchParams.get('q');
  return { query: q && q.trim() ? q : null, filters };
}

interface SaveSearchButtonProps {
  /** Current search URL state — the source of truth for filters. */
  searchParams: URLSearchParams;
  /**
   * Default name suggestion (e.g. the SEO title derived from filters).
   * The user can edit it before saving.
   */
  defaultName: string;
  /** Whether there is anything to save (avoid saving an empty search). */
  hasCriteria: boolean;
}

/**
 * "Save this search" affordance for the search results toolbar
 * (spec §7 #8). Logged-out users are routed to /login with a return
 * path so they come back to the same search. Logged-in users get a
 * name dialog, then a POST to /user/saved-searches.
 */
export function SaveSearchButton({
  searchParams,
  defaultName,
  hasCriteria,
}: SaveSearchButtonProps) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [toast, setToast] = useState<{ severity: 'success' | 'error'; message: string } | null>(
    null
  );

  const mutation = useMutation({
    mutationFn: (body: SavedSearchCreateBody) => userApi.createSavedSearch(body),
    onSuccess: () => {
      setOpen(false);
      setToast({ severity: 'success', message: 'Search saved. Find it under Saved searches.' });
      // Invalidate the saved-searches list so the new entry shows immediately.
      // Without this, the dashboard's cached query (global 5-min staleTime)
      // wouldn't refetch on navigation and the just-saved search would be
      // missing until the cache expired. Mirrors the delete flow in
      // SavedSearchesPage.
      return queryClient.invalidateQueries({ queryKey: ['user', 'saved-searches'] });
    },
    onError: (err: Error) => {
      setToast({ severity: 'error', message: `Couldn’t save search: ${err.message}` });
    },
  });

  function returnPath(): string {
    const qs = searchParams.toString();
    return `/search${qs ? `?${qs}` : ''}`;
  }

  function onClick() {
    if (!isAuthenticated) {
      navigate(`/login?return=${encodeURIComponent(returnPath())}`);
      return;
    }
    setName(defaultName);
    setOpen(true);
  }

  function onSave() {
    const trimmed = name.trim();
    if (!trimmed) return;
    const { query, filters } = searchParamsToSavedSearch(searchParams);
    mutation.mutate({ name: trimmed, query, filters });
  }

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<BookmarkAddIcon />}
        onClick={onClick}
        disabled={!hasCriteria}
        aria-label="Save this search"
      >
        Save search
      </Button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="save-search-title"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="save-search-title">Save this search</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Give this search a name so you can revisit it from your dashboard. You can also turn it
            into an alert later.
          </DialogContentText>
          <TextField
            // Autofocus is appropriate here: the field lives inside a
            // focus-trapped modal dialog opened by an explicit user
            // action, so moving focus to the name input is expected and
            // WCAG-compliant (not a page-load focus steal).
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            fullWidth
            label="Search name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            inputProps={{ maxLength: 120 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && name.trim()) onSave();
            }}
          />
          {mutation.isError ? (
            <Box sx={{ mt: 2 }}>
              <Alert severity="error" role="alert">
                {(mutation.error as Error).message}
              </Alert>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={onSave}
            disabled={!name.trim() || mutation.isPending}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
}

export default SaveSearchButton;
